import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, filter, finalize, map, of, switchMap, tap, withLatestFrom } from 'rxjs';
import { TipologieMezziActions } from './tipologie-mezzi.actions';
import { GaragePort } from '../../core/services/garage/garage.port';
import { LoadingService } from '../../core/services/loading/loading.service';
import { SwalService } from '../../core/services/alerts/swal.service';
import { selectLoaded } from './tipologie-mezzi.selectors';

@Injectable()
export class TipologieMezziEffects {
  private actions$ = inject(Actions);
  private api = inject(GaragePort);
  private store = inject(Store);
  private loading = inject(LoadingService);
  private swalService = inject(SwalService);

  loadList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TipologieMezziActions.loadList),
      withLatestFrom(this.store.select(selectLoaded)),
      filter(([, loaded]) => !loaded),
      switchMap(() =>
        this.api.getTipologieMezzi().pipe(
          map(res => TipologieMezziActions.loadListSuccess({ items: res.content })),
          catchError(err =>
            of(TipologieMezziActions.loadListFailure({ error: err?.message ?? 'Errore caricamento tipologie mezzi' }))
          ),
        )
      )
    )
  );

  create$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TipologieMezziActions.create),
      tap(() => this.loading.show()),
      switchMap(({ tipo }) =>
        this.api.createTipologiaMezzo(tipo).pipe(
          map(id => TipologieMezziActions.createSuccess({ id })),
          catchError(err =>
            of(TipologieMezziActions.createFailure({ error: err?.message ?? 'Errore creazione tipologia mezzo' }))
          ),
          finalize(() => this.loading.hide()),
        )
      )
    )
  );

  reloadAfterCreate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TipologieMezziActions.createSuccess),
      map(() => TipologieMezziActions.loadList()),
    )
  );

  delete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TipologieMezziActions.delete),
      tap(() => this.loading.show()),
      switchMap(({ id }) =>
        this.api.deleteTipologiaMezzo(id).pipe(
          map(() => TipologieMezziActions.deleteSuccess({ id })),
          catchError(err =>
            of(TipologieMezziActions.deleteFailure({ error: err?.message ?? 'Errore eliminazione tipologia mezzo' }))
          ),
          finalize(() => this.loading.hide()),
        )
      )
    )
  );

  showCreateSuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TipologieMezziActions.createSuccess),
        tap(() => this.swalService.success('Tipologia aggiunta con successo'))
      ),
    { dispatch: false }
  );

  showCreateFailureToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TipologieMezziActions.createFailure),
        tap(({ error }) => this.swalService.error(error || 'Errore durante la creazione della tipologia'))
      ),
    { dispatch: false }
  );
}
